import registeredUser from "@assets/header/menu-selected/icon/registered-user-new.svg";
import NDRIcon from "@assets/ndrImages/ndr2026-title-icon.svg";
import MataMataBannerHome from "@components/Blocks/Banners/MataMataBannerHome";
import BudgetPDFBanner from "@components/Blocks/BudgetPDFBanner/BudgetPDFBanner";
import AdsContainer from "@components/Blocks/Container/AdsContainer";
import HorizontalStoryPackage from "@components/Layouts/HomePageStoryPackages/HorizontalStoryPackage";
import useVideoPackage from "@components/Layouts/HomePageStoryPackages/MediaPackages/hooks/useVideoPackage";
import LandscapeVideoPackage from "@components/Layouts/HomePageStoryPackages/MediaPackages/LandscapeVideoPackage";
import PodcastPackage from "@components/Layouts/HomePageStoryPackages/MediaPackages/PodcastPackage";
import PortraitVideoPackage from "@components/Layouts/HomePageStoryPackages/MediaPackages/PortraitVideoPackage";
import MyBHStoryPackage from "@components/Layouts/HomePageStoryPackages/MyBHStoryPackage";
import VerticalStoryPackage from "@components/Layouts/HomePageStoryPackages/VerticalStoryPackage";
import { ENABLE_BUDGET_PDF_BANNER } from "@config/budgetPDF";
import { NDR_PAGE_URL_ALIAS } from "@config/ndrConstants";
import { PodcastBHCustomEventCategory } from "@config/podcastEvent";
import { AdsImu1 } from "@elements/Advertisement/variant/AdsImu";
import { AdsLb2 } from "@elements/Advertisement/variant/AdsLb";
import VideoDialog from "@elements/Player/VideoDialog";
import {
  IS_PLAYWRIGHT_TEST,
  NDR_FEATURE_ENABLE,
} from "@helper/getEnvVariables";
import { addUrlRef } from "@helper/utils";
import type { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import useChartBeatRecommendedArticles from "@hooks/useChartBeatRecommendedArticles";
import { CasBannerWidget } from "@routes/Home/Components/CasBannerWidget";
import TrendingTopics, {
  TrendingTopicItem,
} from "@routes/Home/Components/TrendingTopics";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";

import WidgetWrapper from "./WidgetWrapper";

export type HomePageNewProps = {
  topStoryQueue: ProcessedArticleData[];
  latestFreeArticle: ProcessedArticleData[];
  latestSgArticle: ProcessedArticleData[];
  latestMaArticle: ProcessedArticleData[];
  kakiJalanArticle: ProcessedArticleData[];
  kakiMakanArticle: ProcessedArticleData[];
  dnaDuniaArticle: ProcessedArticleData[];
  latestGayaArticle: ProcessedArticleData[];
  registeredArticle: ProcessedArticleData[];
  latestDuniaArticle: ProcessedArticleData[];
  adsSectionName: string;
  pageAdTargetValue: PageAdTargetingTypeEnum;
  ndrArticle?: ProcessedArticleData[];
  KakiMakanBanner?: string;
  myBHServerArticles?: ProcessedArticleData[];
  trendingTopics?: TrendingTopicItem[];
};
export default function HomePageNew({
  topStoryQueue: rawTopStoryQueue,
  latestFreeArticle: rawLatestFreeArticle,
  ndrArticle,
  latestSgArticle: rawLatestSgArticle,
  latestMaArticle: rawLatestMaArticle,
  kakiJalanArticle: rawKakiJalanArticle,
  kakiMakanArticle: rawKakiMakanArticle,
  dnaDuniaArticle: rawDnaDuniaArticle,
  latestGayaArticle: rawLatestGayaArticle,
  registeredArticle: rawRegisteredArticle,
  latestDuniaArticle: rawLatestDuniaArticle,
  adsSectionName,
  pageAdTargetValue,
  KakiMakanBanner,
  myBHServerArticles,
  trendingTopics = [],
}: HomePageNewProps) {
  const isMyBHEnabled = IS_PLAYWRIGHT_TEST !== "true";
  const { articles: rawMyBHArticles } = useChartBeatRecommendedArticles({
    enabled: isMyBHEnabled,
    initialArticles: myBHServerArticles,
  });
  const homeRef = "home_";
  const topStoryQueue = mapArticlesWithRef(
    rawTopStoryQueue,
    `${homeRef}top_stories`,
  );
  const latestFreeArticle = mapArticlesWithRef(
    rawLatestFreeArticle,
    `${homeRef}terkini`,
  );
  const latestSgArticle = mapArticlesWithRef(
    rawLatestSgArticle,
    `${homeRef}singapura`,
  );
  const latestMaArticle = mapArticlesWithRef(
    rawLatestMaArticle,
    `${homeRef}malaysia`,
  );
  const kakiJalanArticle = mapArticlesWithRef(
    rawKakiJalanArticle,
    `${homeRef}kaki_jalan`,
  );
  const kakiMakanArticle = mapArticlesWithRef(
    rawKakiMakanArticle,
    `${homeRef}kaki_makan`,
  );
  const dnaDuniaArticle = mapArticlesWithRef(
    rawDnaDuniaArticle,
    `${homeRef}dna_dunia`,
  );
  const latestGayaArticle = mapArticlesWithRef(
    rawLatestGayaArticle,
    `${homeRef}gaya_hidup`,
  );
  const registeredArticle = mapArticlesWithRef(
    rawRegisteredArticle,
    `${homeRef}registered`,
  );
  const latestDuniaArticle = mapArticlesWithRef(
    rawLatestDuniaArticle,
    `${homeRef}dunia`,
  );
  const myBHArticles = mapArticlesWithRef(rawMyBHArticles, `${homeRef}untukmu`);

  const { selectedVideo, visible, closeVideoDialog } = useVideoPackage();

  const listingPagesLinks = {
    terkini: {
      destination: "terkini?ref=home_terkini",
    },
    ndr: {
      destination: `${NDR_PAGE_URL_ALIAS}?ref=home_ndr`,
    },
    myBH: {
      destination: "untukmu?ref=home_untukmu",
    },
    singapura: {
      destination: "singapura?ref=home_singapura",
    },
    malaysia: {
      destination: "malaysia?ref=home_malaysia",
    },
    videos: {
      destination: "videos?ref=home_videos",
    },
    kakiJalan: {
      destination: "tags/kaki-jalan?ref=home_kaki_jalan",
    },
    kakiMakan: {
      destination: "tags/kaki-makan?ref=home_kaki_makan",
    },
    dnaDunia: {
      destination: "tags/dna-dunia-anak?ref=home_dna_dunia",
    },
    gayaHidup: {
      destination: "gaya-hidup?ref=home_gaya_hidup",
    },
    registered: {
      destination: "registered?ref=home_registered",
    },
    dunia: {
      destination: "dunia?ref=home_dunia",
    },
  };

  return (
    <>
      <WidgetWrapper>
        <VerticalStoryPackage
          firstCardVariant="featured"
          restCardVariant="2md"
          renderItems={topStoryQueue}
          styles={{
            wrapperStyle: "border-t-0 mt-0 pt-0 md:pt-4 lg:pt-1",
          }}
        />
        {ENABLE_BUDGET_PDF_BANNER && <BudgetPDFBanner />}
      </WidgetWrapper>
      <AdsContainer>
        <AdsLb2
          uniqueSectionName={adsSectionName}
          pageAdTargetType={pageAdTargetValue}
          hasStripeBg={false}
        />
      </AdsContainer>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="featured"
          renderItems={latestFreeArticle}
          styles={{
            wrapperStyle: "border-brand-primary",
            titleStyle: "text-brand-primary",
            cardInnerWrapperStyle:
              "flex-col md:grid md:grid-cols-2  xl:flex xl:flex-row gap-[20px] w-full",
            imageWidth: 284,
            imageHeight: 195,
          }}
          title="Terkini"
          titleLink={listingPagesLinks.terkini}
          moreLink={listingPagesLinks.terkini}
          showBeyondWordsPlayer={true}
        />
        <CasBannerWidget CasBannerWidgetContent={KakiMakanBanner} />
      </WidgetWrapper>
      {ndrArticle && NDR_FEATURE_ENABLE && (
        <WidgetWrapper>
          <VerticalStoryPackage
            firstCardVariant="small"
            restCardVariant="large"
            renderItems={ndrArticle}
            title="Hari Kebangsaan 2026"
            titleLink={listingPagesLinks.ndr}
            moreLink={listingPagesLinks.ndr}
            styles={{
              wrapperStyle: "border-prim-red-300",
              titleStyle: "text-prim-red-300 flex gap-xs items-center pl-xxs",
            }}
            titleIcon={{
              src: NDRIcon,
              width: 38,
              height: 40,
            }}
          />
        </WidgetWrapper>
      )}
      {isMyBHEnabled && (
        <WidgetWrapper>
          <MyBHStoryPackage articles={myBHArticles} />
        </WidgetWrapper>
      )}

      <WidgetWrapper className="lg:pt-5 lg:mb-8">
        <MataMataBannerHome />
      </WidgetWrapper>

      <WidgetWrapper>
        <VerticalStoryPackage
          firstCardVariant="small"
          restCardVariant="large"
          renderItems={latestSgArticle}
          title="Singapura"
          titleLink={listingPagesLinks.singapura}
          moreLink={listingPagesLinks.singapura}
          styles={{
            wrapperStyle: "border-red-100",
            titleStyle: "text-red-100",
          }}
        />
      </WidgetWrapper>
      <AdsContainer>
        <AdsLb2
          uniqueSectionName={adsSectionName}
          pageAdTargetType={pageAdTargetValue}
          hasStripeBg={false}
          index={1}
        />
      </AdsContainer>
      <WidgetWrapper>
        <PortraitVideoPackage />
        <HorizontalStoryPackage
          variant="large"
          renderItems={latestMaArticle}
          title="Malaysia"
          titleLink={listingPagesLinks.malaysia}
          moreLink={listingPagesLinks.malaysia}
          styles={{
            wrapperStyle: "border-pink-600",
            titleStyle: "text-pink-600",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <div className="flex flex-col lg:flex-row gap-xs mb-5">
          <div className="w-full lg:max-w-[645px] xl:max-w-[860px]">
            <LandscapeVideoPackage
              titleLink={{
                ...listingPagesLinks.videos,
                className: "text-brand-primary",
              }}
            />
          </div>
          <AdsContainer className="flex w-full lg:max-w-[320px] justify-center items-center bg-bg-primary">
            <AdsImu1
              uniqueSectionName={adsSectionName}
              pageAdTargetType={pageAdTargetValue}
            />
          </AdsContainer>
        </div>
      </WidgetWrapper>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="small"
          renderItems={kakiJalanArticle}
          title="Kaki Jalan"
          titleLink={listingPagesLinks.kakiJalan}
          moreLink={listingPagesLinks.kakiJalan}
          styles={{
            wrapperStyle: "border-brand-primary",
            titleStyle: "text-brand-primary",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="small"
          renderItems={kakiMakanArticle}
          title="Kaki Makan"
          titleLink={listingPagesLinks.kakiMakan}
          moreLink={listingPagesLinks.kakiMakan}
          styles={{
            wrapperStyle: "border-brand-primary",
            titleStyle: "text-brand-primary",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="small"
          renderItems={dnaDuniaArticle}
          title="DNA Dunia Anak"
          titleLink={listingPagesLinks.dnaDunia}
          moreLink={listingPagesLinks.dnaDunia}
          styles={{
            wrapperStyle: "border-blue-350",
            titleStyle: "text-blue-350",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <VerticalStoryPackage
          firstCardVariant="small"
          restCardVariant="large"
          renderItems={latestGayaArticle}
          title="Gaya Hidup"
          titleLink={listingPagesLinks.gayaHidup}
          moreLink={listingPagesLinks.gayaHidup}
          styles={{
            wrapperStyle: "border-red-600",
            titleStyle: "text-red-600",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="medium"
          renderItems={registeredArticle}
          title="Pengguna Berdaftar"
          titleLink={listingPagesLinks.registered}
          moreLink={listingPagesLinks.registered}
          styles={{
            wrapperStyle: "border-purple-300",
            titleStyle: "flex gap-xs items-center text-purple-300 pl-xxs",
          }}
          titleIcon={{
            src: registeredUser,
            width: 16,
            height: 16,
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <HorizontalStoryPackage
          variant="large"
          renderItems={latestDuniaArticle}
          title="Dunia"
          titleLink={listingPagesLinks.dunia}
          moreLink={listingPagesLinks.dunia}
          styles={{
            wrapperStyle: "border-pink-600",
            titleStyle: "text-pink-600",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <PodcastPackage
          bhCustomEventProps={{
            eventCategory: PodcastBHCustomEventCategory.HOME_PAGE,
            eventAction: "click",
          }}
        />
      </WidgetWrapper>
      <WidgetWrapper>
        <TrendingTopics topics={trendingTopics} />
      </WidgetWrapper>
      {selectedVideo && (
        <VideoDialog
          videoDialog={{
            visible,
            source: selectedVideo.source,
            variant: selectedVideo.variant,
          }}
          handleOnClickCloseDialog={closeVideoDialog}
        />
      )}
    </>
  );
}

function mapArticlesWithRef(articles: ProcessedArticleData[], ref: string) {
  return articles.map((article) => ({
    ...article,
    linkToSection: article.linkToSection
      ? addUrlRef(article.linkToSection, ref)
      : article.linkToSection,
    linkTo: article.linkTo ? addUrlRef(article.linkTo, ref) : article.linkTo,
    urlPath: article.urlPath
      ? addUrlRef(article.urlPath, ref)
      : article.urlPath,
  }));
}
